import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { postOverlay, num, str, dayLabel } from '@/lib/overlayApi';
import type { DateRange } from '@/lib/dateRange';

export interface SkuSeries {
  labels: string[];
  byProduct: Record<string, Array<number | null>>;
}

/** Daily weighted OSA per product, for the compare-trends chart. */
export function useSkuSeries(
  customerName: string,
  productIds: string[],
  city: string | undefined,
  range: DateRange,
  enabled: boolean,
): UseQueryResult<SkuSeries, Error> {
  return useQuery({
    queryKey: ['sku-series', customerName, [...productIds].sort(), city, range],
    enabled: enabled && productIds.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const body: Record<string, unknown> = {
        customer_name: customerName,
        product_ids: productIds,
        window: range,
      };
      if (city) body['city'] = city;

      const res = await postOverlay<Record<string, unknown>>(
        '/v1/availability/competition/sku-series',
        body,
        signal,
      );

      const days = [...new Set(res.rows.map((r) => str(r['day'])))].sort();
      const at = new Map(days.map((d, i) => [d, i]));
      const byProduct: Record<string, Array<number | null>> = {};
      for (const id of productIds) byProduct[id] = days.map(() => null);
      for (const r of res.rows) {
        const id = str(r['product_id']);
        const i = at.get(str(r['day']));
        const target = byProduct[id];
        if (target && i !== undefined) target[i] = num(r['wt_osa']);
      }
      return { labels: days.map(dayLabel), byProduct };
    },
  });
}
